import { roleEnum, users } from '@markku/db'
import { assertNotNil } from '@markku/utils'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'
import { phoneNumberSchema } from './common'

export type Role = (typeof roleEnum.enumValues)[number]

// Role hierarchy: each role inherits permissions from roles below it
// Record ensures all roles are defined (TypeScript enforces completeness)
const ROLE_HIERARCHY: Record<Role, readonly Role[]> = {
  admin: ['admin', 'service_manager', 'officer', 'employee', 'user'],
  officer: ['officer', 'user'],
  service_manager: ['service_manager', 'officer', 'employee', 'user'],
  employee: ['employee', 'user'],
  user: ['user'],
} as const

/**
 * Expands roles array based on hierarchy
 * @example expandRoles(['admin']) // returns ['admin', 'officer', 'user']
 */
export function expandRoles(roles: Role[]): Role[] {
  return [
    ...new Set(
      roles.flatMap((role) => {
        return assertNotNil(
          ROLE_HIERARCHY[role],
          `Role hierarchy ${role} not found`,
        )
      }),
    ),
  ]
}

// Create Zod schemas from Drizzle table schema
export const selectUserSchema = createSelectSchema(users, {
  // Roles isn't automatically inferred as array from the DB definition, so we need to manually define it
  roles: z.enum(roleEnum.enumValues).array(),
})

export const insertUserSchema = createInsertSchema(users, {
  phoneNumber: phoneNumberSchema,
}).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
  emailVerifiedAt: true,
})

// Public user schema (without sensitive data)
export const publicUserSchema = selectUserSchema.omit({
  emailVerifiedAt: true,
})

export const updateUserSchema = insertUserSchema.partial().extend({
  id: z.number().int().positive(),
})

// User with customer memberships schema (for user.me endpoint)
export const selectUserWithMembershipsSchema = selectUserSchema.extend({
  customerMemberships: z
    .array(
      z.object({
        id: z.number(),
        customerId: z.number(),
        role: z.enum(['owner', 'member']),
        customer: z.object({
          id: z.number(),
          name: z.string(),
        }),
      }),
    )
    .optional(),
})

// Type exports
export type User = z.infer<typeof selectUserSchema>
export type UserWithMemberships = z.infer<
  typeof selectUserWithMembershipsSchema
>
export type PublicUser = z.infer<typeof publicUserSchema>
export type CreateUser = z.infer<typeof insertUserSchema>
export type UpdateUser = z.infer<typeof updateUserSchema>

// Utility functions for role management
export function hasRole(userRoles: Role[], requiredRole: Role): boolean {
  return userRoles.includes(requiredRole)
}

export function hasAnyRole(userRoles: Role[], requiredRoles: Role[]): boolean {
  return requiredRoles.some((role) => userRoles.includes(role))
}

// Marketing attribution data schema
export const acquisitionDataSchema = z.object({
  utmParams: z.record(z.string()).optional(),
  referrer: z.string().optional(),
  landingPage: z.string().optional(),
  timestamp: z.string(),
  device: z.object({
    screenResolution: z.string(),
    language: z.string(),
    timeZone: z.string(),
  }),
})
export type AcquisitionData = z.infer<typeof acquisitionDataSchema>

// OTP-related schemas (TODO remove / refactor these)
export const requestOtpSchema = z.object({
  email: z.string().email(),
  turnstileToken: z.string().min(1).optional(),
})

export const verifyOtpSchema = z.object({
  email: z.string().email(),
  token: z.string().min(6).max(6), // 6-digit OTP
  app: z.enum(['erp', 'employee', 'resident']),
  marketingConsent: z.boolean().optional(),
  acquisitionData: acquisitionDataSchema.optional(),
})
export type RequestOtp = z.infer<typeof requestOtpSchema>
export type VerifyOtp = z.infer<typeof verifyOtpSchema>

// Google OAuth schemas
export const googleCallbackSchema = z.object({
  code: z.string().min(1),
  app: z.enum(['resident']),
  acquisitionData: acquisitionDataSchema.optional(),
})

export const googleLoginResponseSchema = z.object({
  url: z.string().url(),
})

export type GoogleCallback = z.infer<typeof googleCallbackSchema>
export type GoogleLoginResponse = z.infer<typeof googleLoginResponseSchema>
